/* AUTO-SYNCED from PsyKit.io/lib/haptics/gamepad-poll.ts — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

/**
 * Minimal helpers over the Gamepad API for the haptics hook.
 *
 * Two browser quirks to know about:
 *  - A paired controller is NOT exposed by navigator.getGamepads() until the
 *    user presses a button on it (security). After that, the `gamepadconnected`
 *    event fires.
 *  - navigator.getGamepads() returns a snapshot array of length 4 (or 8) with
 *    null gaps. Indices are stable per device but the array isn't packed.
 */

export function isGamepadApiSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.getGamepads === "function"
  );
}

interface DualRumbleParams {
  duration: number;
  strongMagnitude: number;
  weakMagnitude: number;
  startDelay?: number;
}

interface MinimalActuator {
  playEffect: (type: string, params: DualRumbleParams) => Promise<unknown>;
  reset?: () => Promise<unknown>;
}

export interface UsableGamepad {
  gamepad: Gamepad;
  actuator: MinimalActuator;
}

/**
 * Picks the first connected gamepad whose vibrationActuator can play
 * "dual-rumble". Returns null when nothing usable is connected.
 */
export function firstUsableGamepad(): UsableGamepad | null {
  if (!isGamepadApiSupported()) return null;
  const pads = navigator.getGamepads();
  for (const pad of pads) {
    if (!pad) continue;
    // Current spec: gamepad.vibrationActuator. Older draft used hapticActuators[0].
    // We probe defensively so behavior is identical across Chromium versions.
    const padAny = pad as unknown as {
      vibrationActuator?: unknown;
      hapticActuators?: unknown[];
    };
    const candidate =
      (padAny.vibrationActuator as MinimalActuator | undefined) ??
      (padAny.hapticActuators?.[0] as MinimalActuator | undefined);
    if (!candidate || typeof candidate.playEffect !== "function") continue;
    return { gamepad: pad, actuator: candidate };
  }
  return null;
}

/**
 * Trims the verbose " (Vendor: 054c Product: 0ce6)" tail from a gamepad id
 * so the UI badge reads cleanly: "DualSense Wireless Controller".
 */
export function gamepadLabel(gamepad: Gamepad | null): string | null {
  if (!gamepad) return null;
  const cleaned = gamepad.id.replace(/\s*\([^)]*\)\s*$/, "").trim();
  return cleaned.length > 0 ? cleaned : gamepad.id;
}
